import { Component, DestroyRef, input, output, ViewChild } from '@angular/core';
import { ConfirmationModalComponent } from '../../../../parts/confirmation-modal/confirmation-modal.component';
import { InfoModalComponent } from '../../../../parts/info-modal/info-modal.component';
import { MemberForm } from '../../../../forms/member/member.form/member.form';
import { BootstrapService } from '../../../../core/services/bootstrap.service';
import { OrganizationModel } from '../../../../core/models/data/organization.model';
import { MemberModel } from '../../../../core/models/data/member.model';
import { ModalActions, ModalTypes } from '../../../../core/enums/base.enums';
import { FormState } from '../../../../core/enums/forms.enums';
import { ServiceResultModel } from '../../../../core/models/service/service-result.model';
import { GenericMessagingConstants, MembersMessagingConstants } from '../../../../core/constants/messaging.constants';
import { EscCloseDirective } from '../../../../core/directives/esc-close.directive';
import { BaseFormComponent } from '../../../../core/base-classes/base-form-component';
import { CanComponentDeactivate } from '../../../../core/routing/can-deactivate.guard';
import { DirtyFormRegistryService } from '../../../../core/services/dirty-form-registry.service';

@Component({
  selector: 'ms-member-form-modal',
  imports: [ConfirmationModalComponent, InfoModalComponent, MemberForm, EscCloseDirective],
  templateUrl: './member-form-modal.component.html',
})
export class MemberFormModalComponent extends BaseFormComponent implements CanComponentDeactivate {
  private resetFormOnConfirm: boolean = true;

  public organization = input<OrganizationModel | undefined | null>(null);
  public member = input<MemberModel | undefined>(undefined);
  public formState = input<FormState>(FormState.Create);
  public onSuccessConfirm = output();

  public FormState = FormState;

  @ViewChild(MemberForm) memberFormRef!: MemberForm;
  @ViewChild('memberFormInfoModalRef') memberFormInfoModalRef!: InfoModalComponent;
  @ViewChild('memberFormConfirmationModalRef') memberFormConfirmationModalRef!: ConfirmationModalComponent;
  @ViewChild('editMemberConfirmationModalRef') editMemberConfirmationModalRef!: ConfirmationModalComponent;

  constructor(
    private bootstrapService: BootstrapService,
    private dirtyFormRegistry: DirtyFormRegistryService,
    private destroyRef: DestroyRef
  ) {
    super();
    this.destroyRef.onDestroy(() => this.dirtyFormRegistry.unregister(this));
  }

  public openMemberModal(): void {
    this.submitSucceeded = false;
    this.dirtyFormRegistry.register(this);
    this.bootstrapService.manageModal('#memberFormModal', ModalActions.Show);

    if (this.formState() === FormState.Update) {
      this.memberFormRef.populateFormForEdit();
    }
  }

  public onCancelRequested(): void {
    if (this.memberFormRef.memberForm.dirty) {
      this.memberFormConfirmationModalRef.modalType.set(ModalTypes.Question);
      this.memberFormConfirmationModalRef.message.set(GenericMessagingConstants.GENERIC_ABORT_CONFIRMATION());
      this.bootstrapService.manageModal('#memberFormConfirmationModal', ModalActions.Show);
      this.bootstrapService.manageModal('#memberFormModal', ModalActions.Hide);
    } else {
      this.bootstrapService.manageModal('#memberFormModal', ModalActions.Hide);
      this.dirtyFormRegistry.unregister(this);
    }
  }

  public canDeactivate(): boolean | Promise<boolean> {
    return this.awaitLeaveConfirmation(this.memberFormRef.memberForm.dirty && !this.submitSucceeded, () => {
      this.memberFormConfirmationModalRef.modalType.set(ModalTypes.Question);
      this.memberFormConfirmationModalRef.message.set(GenericMessagingConstants.GENERIC_ABORT_CONFIRMATION());
      this.bootstrapService.manageModal('#memberFormModal', ModalActions.Hide);
      this.bootstrapService.manageModal('#memberFormConfirmationModal', ModalActions.Show);
    });
  }

  public confirmationModalConfirmed(): void {
    this.bootstrapService.manageModal('#memberFormConfirmationModal', ModalActions.Hide);
    this.memberFormRef.resetForm();
    this.dirtyFormRegistry.unregister(this);
    this.resolvePendingLeave(true);
  }

  public confirmationModalCancelled(): void {
    this.bootstrapService.manageModal('#memberFormModal', ModalActions.Show);
    this.bootstrapService.manageModal('#memberFormConfirmationModal', ModalActions.Hide);
    this.resolvePendingLeave(false);
  }

  public infoModalConfirmed(): void {
    if (this.resetFormOnConfirm === true) {
      this.submitSucceeded = false;
      this.memberFormRef.resetForm();
      this.dirtyFormRegistry.unregister(this);
      this.onSuccessConfirm.emit();
    } else {
      this.bootstrapService.manageModal('#memberFormModal', ModalActions.Show);
    }
  }

  public onMemberResult(result: ServiceResultModel): void {
    this.resetFormOnConfirm = result.success!;
    if (result.success === true) {
      this.submitSucceeded = true;
    }

    this.memberFormInfoModalRef.message.set(result.message);

    if (result.success === true) {
      this.memberFormInfoModalRef.modalType.set(ModalTypes.Success);
    } else {
      this.memberFormInfoModalRef.modalType.set(ModalTypes.Failure);
    }

    this.bootstrapService.manageModal('#memberFormModal', ModalActions.Hide);
    this.bootstrapService.manageModal('#memberFormInfoModal', ModalActions.Show);
  }

  public onEditConfirmationRequested(): void {
    const memberData = this.member();
    const messageData = { firstName: memberData?.firstName ?? '', lastName: memberData?.lastName ?? '' };
    const message = MembersMessagingConstants.MEMBERS_EDIT_CONFIRMATION().replaceValuesWithObject(messageData);

    this.editMemberConfirmationModalRef.modalType.set(ModalTypes.Question);
    this.editMemberConfirmationModalRef.message.set(message);

    this.bootstrapService.manageModal('#memberFormModal', ModalActions.Hide);
    this.bootstrapService.manageModal('#editMemberConfirmationModal', ModalActions.Show);
  }

  public async editConfirmationModalConfirmed(): Promise<void> {
    this.bootstrapService.manageModal('#editMemberConfirmationModal', ModalActions.Hide);
    await this.memberFormRef.updateMember();
  }

  public editConfirmationModalCancelled(): void {
    this.bootstrapService.manageModal('#editMemberConfirmationModal', ModalActions.Hide);
    this.bootstrapService.manageModal('#memberFormModal', ModalActions.Show);
  }
}
